// src/components/map/MapPin.tsx
//
// 36px circular pin used by MapExplorationView. Renders a Google
// Places photo when a photo_reference is available; otherwise
// falls back to a deterministic colored disc keyed off the place
// id (so the same place always gets the same fallback color
// across renders, which feels less random to the user than pure
// random-per-render coloring).
//
// Active state (tapped/selected): grows to 44px and gets a yellow
// outline ring, mirroring the "current waypoint" affordance in
// WalkingTourView.
//
// Photo URL is built via the placePhotoProxy Cloud Function,
// which holds the Places API key server-side. The same pattern is
// used by PlacesResultCard.

import React, { useState } from 'react';
import { Box } from '@mui/material';
import { PlaceOutlined } from '@mui/icons-material';

const PROJECT_ID =
  (import.meta as unknown as { env?: Record<string, string> }).env
    ?.VITE_FIREBASE_PROJECT_ID || 'travelx-46b7a';

/** Build the proxied Places photo URL for a given photo
 *  reference. The proxy adds the API key server-side and serves
 *  the bytes back, so the client never sees the key. */
function photoProxyUrl(photoRef: string, maxwidth = 240): string {
  return (
    `https://us-central1-${PROJECT_ID}.cloudfunctions.net/placePhotoProxy` +
    `?ref=${encodeURIComponent(photoRef)}&maxwidth=${maxwidth}`
  );
}

/** Hash-fold a string into a 0..360 hue for fallback colors. The
 *  golden-ratio multiplier gives a pleasing spread when many pins
 *  are on screen. */
function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  // Map to [0, 360). Math.abs handles the negative-int wrap-around.
  return Math.abs(h) % 360;
}

interface MapPinProps {
  placeId: string;
  photoRef?: string;
  active?: boolean;
}

export const MapPin: React.FC<MapPinProps> = ({
  placeId,
  photoRef,
  active = false,
}) => {
  // Track photo load failures so we can fall back to the colored
  // disc rather than rendering a broken image. The proxy is well-
  // behaved but Places photo refs occasionally 404 and we don't
  // want a busted thumbnail breaking the pin layout.
  const [imgFailed, setImgFailed] = useState(false);

  const size = active ? 44 : 36;
  const fallbackHue = hashHue(placeId);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        // 3px white border + drop shadow makes the pin pop on any
        // map background (light streets, dark parks, satellite).
        border: '3px solid white',
        boxShadow: active
          ? '0 0 0 2px #fbbf24, 0 4px 12px rgba(0,0,0,0.4)'
          : '0 2px 8px rgba(0,0,0,0.3)',
        overflow: 'hidden',
        position: 'relative',
        // Fallback disc color when there's no photo / photo failed
        // to load. Saturation + lightness chosen for legibility on
        // both light and dark map themes.
        bgcolor: `hsl(${fallbackHue}, 60%, 45%)`,
        transition: 'all 0.15s ease',
        // Make sure the pin element accepts pointer events even if
        // a parent disabled them (e.g. during a drawer drag, the
        // map ancestor sets touch-action). The Marker layer in
        // @vis.gl/react-google-maps handles tap propagation.
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {photoRef && !imgFailed ? (
        <Box
          component="img"
          src={photoProxyUrl(photoRef, active ? 320 : 240)}
          loading="lazy"
          // No alt text — a place photo is presentational on the
          // pin; the place name is rendered separately as the
          // accessible label by the AdvancedMarker wrapper.
          alt=""
          onError={() => setImgFailed(true)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <PlaceOutlined
          sx={{
            color: 'white',
            fontSize: active ? 22 : 18,
            // Slight drop shadow on the icon so it pops off the
            // colored disc.
            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.3))',
          }}
        />
      )}
    </Box>
  );
};

/** Small, muted variant for "My Pins" overlay. Always visible
 *  underneath the active layer's pins so users can see their
 *  saved places in context. Same visual language as MapPin
 *  (circle + border) but smaller and color-neutral. */
export const MyPinDot: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: 'rgba(60, 60, 60, 0.9)',
      border: '2px solid white',
      boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
    }}
  />
);
